import { useState, useEffect, useCallback } from 'react';
import ThemeContext from './ThemeContext.js';

const STORAGE_KEY = 'aims-os-theme';

const getSystemTheme = () => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const resolvePreference = (pref) => (pref === 'system' ? getSystemTheme() : pref);

/**
 * Provides dark/light/system theme management across the application.
 *
 * - `theme`: the user's stored preference — `'light'` | `'dark'` | `'system'`
 * - `resolvedTheme`: the actually applied theme — always `'light'` | `'dark'`
 * - `toggleTheme`: cycles light → dark → system → light
 *
 * When embedded in the Shell, the Shell's ThemeProvider is already mounted
 * higher in the tree — child apps simply consume ThemeContext without
 * mounting a second provider.
 */
const ThemeProvider = ({ children, initialTheme }) => {
  const [theme, setThemeState] = useState(() => {
    if (initialTheme) return initialTheme;
    try {
      return localStorage.getItem(STORAGE_KEY) || 'system';
    } catch {
      return 'system';
    }
  });

  const [resolvedTheme, setResolvedTheme] = useState(() => resolvePreference(theme));

  useEffect(() => {
    const applyDark = (isDark) => {
      document.documentElement.classList.toggle('dark', isDark);
      setResolvedTheme(isDark ? 'dark' : 'light');
    };

    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      applyDark(mq.matches);
      const handler = (e) => applyDark(e.matches);
      mq.addEventListener('change', handler);
      try { localStorage.setItem(STORAGE_KEY, 'system'); } catch { /* unavailable */ }
      return () => mq.removeEventListener('change', handler);
    }

    applyDark(theme === 'dark');
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* unavailable */ }
  }, [theme]);

  const setTheme = useCallback((next) => setThemeState(next), []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
